import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Button,
  IconButton,
  TextField,
  Collapse,
} from '@mui/material';
import { Add, Chat as ChatIcon } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useNavigate } from 'react-router';
import ModalForms from '../Shared/Modals/index.js';
import { AiActions, LeadFormActions } from '../../Services/_actions/index.js';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CampaignAssignedDropdown from '../Shared/CampaignAssignmentDropdown/index.tsx';

interface ApestmicAiSidebarProps {
  isMobile: boolean;
  onClose?: () => void;
  navigate?: (path: string) => void;
  activeTab?: number;
  onTabChange?: (i: number) => void;
  companyName?: string;
  onNewConversation?: () => void;
  conversationList?: any[];
  onSelectConversation?: (conversationId: string) => void;
  selectedConversationId?: string;
  projectList?: Project[];
  selectedProjectId?: string | null;
  onSelectProject?: (projectId: string | null) => void;
  onCreateProject?: (name: string) => void;
  onLeadFormFetched?: (leadFormId: number) => void;
  onCampaignSelected?: (campaign: any) => void;
}

interface Project {
  id: string;
  name: string;
  emoji?: string;
  chatCount?: number;
  chats?: any[];
}

// ── ProjectRow ───────────────────────────────────────────────────────────────
interface ProjectRowProps {
  project: Project;
  expandedProjects: Record<string, boolean>;
  toggleProject: (id: string) => void;
  selectedProjectId?: string | null;
  selectedConversationId?: string;
  onSelectProject?: (id: string | null) => void;
  onNewConversation?: () => void;
  onSelectConversation?: (id: string) => void;
}

const ProjectRow: React.FC<ProjectRowProps> = ({
  project,
  expandedProjects,
  toggleProject,
  selectedProjectId,
  selectedConversationId,
  onSelectProject,
  onNewConversation,
  onSelectConversation,
}) => {
  const isExpanded = !!expandedProjects[project.id];
  const projectChats = project.chats || [];

  return (
    <Box sx={{ mb: 0.3 }}>
      <ListItemButton
        onClick={() => toggleProject(project.id)}
        sx={{
          borderRadius: '8px',
          py: 0.7,
          px: 1,
          bgcolor: selectedProjectId === project.id ? 'rgba(0,0,0,0.05)' : 'transparent',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
        }}
      >
        <ListItemIcon sx={{ minWidth: 30 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '6px',
              bgcolor: 'rgba(0,0,0,0.06)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
            }}
          >
            {project.emoji || '📁'}
          </Box>
        </ListItemIcon>

        {/* Name + chat count on same line */}
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 400,
              color: '#111',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: 1,
            }}
          >
            {project.name}
          </Typography>
          {project.chatCount !== undefined && (
            <Typography sx={{ fontSize: 10, color: '#aaa', flexShrink: 0 }}>
              {project.chatCount} chat{project.chatCount !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>

        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onSelectProject?.(project.id);
            onNewConversation?.();
          }}
          sx={{
            p: 0.3,
            ml: 0.5,
            color: '#bbb',
            '&:hover': { color: '#000', bgcolor: 'rgba(0,0,0,0.06)' },
          }}
        >
          <Add sx={{ fontSize: 13 }} />
        </IconButton>

        {isExpanded ? (
          <ExpandLessIcon sx={{ fontSize: 14, color: '#bbb' }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 14, color: '#bbb' }} />
        )}
      </ListItemButton>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <List dense disablePadding sx={{ pl: 2, mt: 0.2 }}>
          {projectChats.length === 0 ? (
            <ListItem sx={{ py: 0.4, px: 1 }}>
              <Typography sx={{ fontSize: 11, color: '#ccc', fontStyle: 'italic' }}>
                No chats yet
              </Typography>
            </ListItem>
          ) : (
            projectChats.map((chat: any, idx: number) => (
              <ListItem key={chat.conversation_id || idx} disablePadding sx={{ mb: 0.2 }}>
                <ListItemButton
                  selected={chat.conversation_id === selectedConversationId}
                  onClick={() => {
                    onSelectProject?.(project.id);
                    onSelectConversation?.(chat.conversation_id);
                  }}
                  sx={{
                    borderRadius: '7px',
                    py: 0.5,
                    px: 1,
                    '&.Mui-selected': { bgcolor: 'rgba(0,0,0,0.06)' },
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 24 }}>
                    <ChatIcon sx={{ fontSize: 11, color: '#bbb' }} />
                  </ListItemIcon>
                  {/* Chat title + date on same line */}
                  <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden', flex: 1, gap: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 400,
                        color: '#222',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        flex: 1,
                      }}
                    >
                      {chat.query || 'Conversation'}
                    </Typography>
                    {chat.created_at && (
                      <Typography sx={{ fontSize: 10, color: '#bbb', flexShrink: 0 }}>
                        {new Date(chat.created_at).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                </ListItemButton>
              </ListItem>
            ))
          )}
        </List>
      </Collapse>
    </Box>
  );
};

// ── AiSidebar ────────────────────────────────────────────────────────────────
const PROJECT_PREVIEW_COUNT = 3;
const COLLAPSED_HEIGHT = PROJECT_PREVIEW_COUNT * 48;

const AiSidebar: React.FC<ApestmicAiSidebarProps> = ({
  isMobile,
  onClose,
  activeTab,
  onTabChange,
  companyName,
  onNewConversation,
  conversationList = [],
  onSelectConversation,
  selectedConversationId,
  projectList = [],
  selectedProjectId,
  onSelectProject,
  onCreateProject,
  onLeadFormFetched,
  onCampaignSelected,
}) => {
  const navigate = useNavigate();
  const [createProjectModal, setCreateProjectModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const action = AiActions();
  const actions = LeadFormActions();
  const [expandedProjects, setExpandedProjects] = useState<Record<string, boolean>>({});
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<any>({});
  // const [showCampaignDropdown, setShowCampaignDropdown] = useState(true);
  const userInfo = localStorage?.getItem('userInfo');
  const CampanyID = userInfo ? JSON.parse(userInfo)?.profile?.organisation?.organisation_id : null;
  const ORGANIZATION_ID = CampanyID;
  // useEffect(() => {
  //   if (selectedCampaign?.proposal_id) {
  //     setShowCampaignDropdown(false);
  //   }
  // }, [selectedCampaign]);

  const fetchLeadForm = async (proposalId: string) => {
    try {
      const response = await actions.getFormsByCampaignId(proposalId);
      if (response?.data) {
        const leadFormData = response.data;
        // Get first key's leads_form_id
        const firstKey = Object.keys(leadFormData)[0];
        const firstForm = leadFormData[firstKey];
        if (firstForm?.leads_form_id) {
          onLeadFormFetched?.(firstForm.leads_form_id);
        }
      }
    } catch (error) {
      console.error('Error fetching lead form:', error);
    }
  };
  useEffect(() => {
    if (selectedCampaign?.proposal_id) {
      fetchLeadForm(selectedCampaign.proposal_id);
    }
  }, [selectedCampaign]);
  const toggleProject = (projectId: string) => {
    setExpandedProjects((prev) => ({ ...prev, [projectId]: !prev[projectId] }));
  };

  const handleCreateProject = () => {
    if (newProjectName.trim()) {
      onCreateProject?.(newProjectName.trim());
      setNewProjectName('');
      setCreateProjectModal(false);
    }
  };

  const hasMore = (projectList || []).length > PROJECT_PREVIEW_COUNT;
  const extraCount = Math.max(0, (projectList || []).length - PROJECT_PREVIEW_COUNT);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fff',
        borderRadius: isMobile ? 0 : '12px',
        overflow: 'hidden',
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          bgcolor: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            bgcolor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <AutoAwesomeIcon sx={{ color: '#fff', fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: 13, lineHeight: 1.2 }}>
            Machadalo Studio
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' }}>
            Workspace
          </Typography>
        </Box>

        {/* History Button */}
        <Button
          size="small"
          onClick={() => navigate('/r/machadalostudio/history')}
          sx={{
            minWidth: 'auto',
            px: 1.2,
            py: 0.5,
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: '8px',
            textTransform: 'none',
            fontSize: 11,
            bgcolor: 'rgba(255,255,255,0.08)',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.15)',
            },
          }}
        >
          History
        </Button>


        {isMobile && onClose && (
          <IconButton onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      {/* <Box sx={{ px: 1.5, mt: 1 }}>
        {showCampaignDropdown ? (
          <CampaignAssignedDropdown
            value={selectedCampaign}
            onChange={(val) => {
              setSelectedCampaign(val);
              onCampaignSelected?.(val);
            }}
            organisationId={ORGANIZATION_ID}
            autoSelectValue={true}
          />
        ) : (
          <Typography
            sx={{
              px: 1.5,
              py: 1,
              fontSize: 12,
              fontWeight: 500,
              color: '#111',
              borderRadius: '8px',
              bgcolor: 'rgba(0,0,0,0.04)',
            }}
          >
            {selectedCampaign?.name || 'Selected Campaign'}
          </Typography>
        )}
      </Box> */}
      <Box sx={{ px: 1.5, mt: 1 }}>
        <CampaignAssignedDropdown
          value={selectedCampaign}
          onChange={(val) => {
            setSelectedCampaign(val);
            onCampaignSelected?.(val);
          }}
          organisationId={ORGANIZATION_ID}
          autoSelectValue={true}
        />
      </Box>
      {/* ── New Chat + Add Project ── */}
      <Box sx={{ px: 1.5, pt: 1.5, mb: 0.5 }}>
        <Button
          fullWidth
          onClick={onNewConversation}
          startIcon={<Add sx={{ fontSize: 15 }} />}
          sx={{
            justifyContent: 'flex-start',
            textTransform: 'none',
            fontSize: 13,
            fontWeight: 400,
            color: '#111',
            px: 1.5,
            py: 0.9,
            borderRadius: '8px',
            bgcolor: 'transparent',
            boxShadow: 'none',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
          }}
        >
          New chat
        </Button>

        <Button
          fullWidth
          onClick={() => setCreateProjectModal(true)}
          startIcon={<Add sx={{ fontSize: 13 }} />}
          sx={{
            justifyContent: 'flex-start',
            textTransform: 'none',
            fontSize: 13,
            fontWeight: 400,
            color: '#111',
            px: 1.5,
            py: 0.9,
            borderRadius: '8px',
            bgcolor: 'transparent',
            boxShadow: 'none',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
          }}
        >
          Add project
        </Button>
      </Box>

      <Box sx={{ mx: 2, borderTop: '1px solid #f0f0f0' }} />

      {/* ── Projects ── */}
      <Box sx={{ px: 1.5, mt: 1.2 }}>
        <Typography
          sx={{
            color: '#000000',
            fontSize: 9.5,
            fontWeight: 800,
            letterSpacing: 1.4,
            textTransform: 'uppercase',
            px: 1,
            mb: 0.8,
          }}
        >
          Projects
        </Typography>

        {/* Single unified scrollable container — no frozen rows */}
        <Box
          sx={{
            maxHeight: showAllProjects ? 300 : `${COLLAPSED_HEIGHT}px`,
            overflowY: showAllProjects ? 'auto' : 'hidden',
            transition: 'max-height 0.25s ease',
            pr: 0.3,
            '&::-webkit-scrollbar': { width: 3 },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#e0e0e0', borderRadius: 4 },
            '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
          }}
        >
          <List dense disablePadding>
            {(projectList || []).map((project) => (
              <ProjectRow
                key={project.id}
                project={project}
                expandedProjects={expandedProjects}
                toggleProject={toggleProject}
                selectedProjectId={selectedProjectId}
                selectedConversationId={selectedConversationId}
                onSelectProject={onSelectProject}
                onNewConversation={onNewConversation}
                onSelectConversation={onSelectConversation}
              />
            ))}
          </List>
        </Box>

        {hasMore && (
          <Button
            fullWidth
            onClick={() => setShowAllProjects((prev) => !prev)}
            startIcon={
              showAllProjects
                ? <ExpandLessIcon sx={{ fontSize: 12 }} />
                : <ExpandMoreIcon sx={{ fontSize: 12 }} />
            }
            sx={{
              justifyContent: 'flex-start',
              textTransform: 'none',
              fontSize: 11,
              fontWeight: 400,
              color: '#aaa',
              px: 1.5,
              py: 0.5,
              mt: 0.3,
              borderRadius: '8px',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.04)', color: '#555' },
            }}
          >
            {showAllProjects ? 'View less' : `View ${extraCount} more project${extraCount !== 1 ? 's' : ''}`}
          </Button>
        )}
      </Box>

      <Box sx={{ mx: 2, my: 1, borderTop: '1px solid #f0f0f0' }} />

      {/* ── History ── */}
      <Box sx={{ px: 1.5, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Typography
          sx={{
            color: '#000000',
            fontSize: 9.5,
            fontWeight: 800,
            letterSpacing: 1.4,
            textTransform: 'uppercase',
            mb: 0.8,
            px: 1,
          }}
        >
          History
        </Typography>

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            pr: 0.3,
            '&::-webkit-scrollbar': { width: 3 },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#e0e0e0', borderRadius: 4 },
          }}
        >
          {conversationList.length === 0 ? (
            <Typography sx={{ color: '#ccc', fontSize: 12, fontStyle: 'italic', px: 1 }}>
              No conversations yet
            </Typography>
          ) : (
            <List dense disablePadding>
              {conversationList.map((conv, i) => (
                <ListItem key={conv.id || i} disablePadding sx={{ mb: 0.2 }}>
                  <ListItemButton
                    selected={conv.conversation_id === selectedConversationId}
                    onClick={() => onSelectConversation?.(conv.conversation_id)}
                    sx={{
                      borderRadius: '8px',
                      py: 0.7,
                      px: 1,
                      '&.Mui-selected': { bgcolor: 'rgba(0,0,0,0.05)' },
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                    }}
                  >
                    {/* Title + date on same line */}
                    <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden', width: '100%', gap: 1 }}>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 400,
                          color: '#111',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flex: 1,
                        }}
                      >
                        {conv.query || 'Conversation'}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: '#bbb', flexShrink: 0 }}>
                        {conv.created_at ? new Date(conv.created_at).toLocaleDateString() : ''}
                      </Typography>
                    </Box>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Box>

      {/* ── Create Project Modal ── */}
      <ModalForms
        show={createProjectModal}
        handleClose={() => { setCreateProjectModal(false); setNewProjectName(''); }}
        modalHeader="Create New Project"
        size="md"
        modalBody={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ fontSize: 13, color: '#555' }}>
              Enter a project name. Related conversations will be organized inside it.
            </Typography>
            <TextField
              fullWidth
              autoFocus
              size="small"
              label="Project Name"
              placeholder="e.g. Brand Strategy, Q3 Campaign..."
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreateProject(); }}
              sx={{ '& .MuiInputBase-input': { fontSize: 13 } }}
            />
          </Box>
        }
        modalFooter={
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button
              size="small"
              onClick={() => { setCreateProjectModal(false); setNewProjectName(''); }}
              sx={{
                textTransform: 'none', fontSize: 13, color: '#555',
                border: '1px solid #ddd', borderRadius: '8px', px: 2,
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              disabled={!newProjectName.trim()}
              onClick={handleCreateProject}
              variant="contained"
              sx={{ textTransform: 'none', fontSize: 13, borderRadius: '8px', px: 2, boxShadow: 'none' }}
            >
              Create Project
            </Button>
          </Box>
        }
      />
    </Box>
  );
};

export default AiSidebar;